import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Activity,
  CheckCircle2,
  Shield,
  Swords,
  Target,
  Trophy,
} from "lucide-react";
import { cn } from "@v2/lib/utils";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@v2/components/ui/card";
import { Separator } from "@v2/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@v2/components/ui/select";
import { formatDateTime } from "../../lib/format";
import { currentIntlLocale } from "../../lib/i18n";
import { useWorkspaceData } from "../../lib/workspace-data";
import { EmptyState, SectionLoading } from "../../components/state";
import { StatStrip } from "./StatStrip";
import { type RangeKey, rangeBounds, rangeLabel, RANGE_KEYS } from "./stats-utils";
import { PlayersFullTable } from "./PlayersFullTable";

function MonthlyBreakdownChart({
  data,
}: {
  data: Array<{ key: string; label: string; wins: number; draws: number; losses: number; total: number }>;
}) {
  const { t } = useTranslation("stats");
  const max = Math.max(1, ...data.map((d) => d.total));
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-6 gap-2 h-40 items-end">
        {data.map((bucket) => {
          const heightPct = (bucket.total / max) * 100;
          const wPct = bucket.total > 0 ? (bucket.wins / bucket.total) * 100 : 0;
          const dPct = bucket.total > 0 ? (bucket.draws / bucket.total) * 100 : 0;
          const lPct = bucket.total > 0 ? (bucket.losses / bucket.total) * 100 : 0;
          return (
            <div key={bucket.key} className="flex flex-col items-center justify-end gap-1 h-full">
              <span className="text-xs text-muted-foreground tabular-nums">{bucket.total || ""}</span>
              <div className="w-full max-w-10 rounded-lg overflow-hidden flex flex-col gap-0.5" style={{ height: `${heightPct}%`, minHeight: bucket.total > 0 ? 8 : 0 }}>
                {bucket.losses > 0 && <div className="bg-destructive" style={{ height: `${lPct}%` }} />}
                {bucket.draws > 0 && <div className="bg-warning" style={{ height: `${dPct}%` }} />}
                {bucket.wins > 0 && <div className="bg-success" style={{ height: `${wPct}%` }} />}
              </div>
              <span className="text-xs text-muted-foreground capitalize">{bucket.label}</span>
            </div>
          );
        })}
      </div>
      <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-sm bg-success" /> {t("team.win")}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-sm bg-warning" /> {t("team.draw")}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-sm bg-destructive" /> {t("team.loss")}
        </span>
      </div>
    </div>
  );
}

export function TeamStatsTab({
  teamId,
  allSeasons = false,
  referenceNow,
  competitionId = null,
}: {
  teamId: string;
  allSeasons?: boolean;
  referenceNow?: number;
  competitionId?: string | null;
}) {
  const { t } = useTranslation("stats");
  const { matches, matchStats, isLoading } = useWorkspaceData(teamId, { allSeasons, competitionId });
  const [range, setRange] = useState<RangeKey>("all");
  const [resultFilter, setResultFilter] = useState<"all" | "win" | "draw" | "loss">("all");

  const filtered = useMemo(() => {
    const { from, to } = rangeBounds(range, referenceNow);
    return matches
      .filter((match) => {
        if (!match.starts_at) return range === "all";
        const ts = new Date(match.starts_at).getTime();
        if (from !== null && ts < from) return false;
        if (to !== null && ts > to) return false;
        return true;
      })
      .filter((match) => match.our_score !== null && match.opponent_score !== null)
      .sort((a, b) => String(b.starts_at || "").localeCompare(String(a.starts_at || "")));
  }, [matches, range, referenceNow]);

  const goalsByMatch = useMemo(() => {
    const map = new Map<string, number>();
    for (const stat of matchStats) {
      map.set(stat.match_id, (map.get(stat.match_id) || 0) + (stat.goals || 0));
    }
    return map;
  }, [matchStats]);

  const totals = useMemo(() => {
    let wins = 0;
    let draws = 0;
    let losses = 0;
    let goalsFor = 0;
    let goalsAgainst = 0;
    let cleanSheets = 0;
    let homeGames = 0;
    let homeWins = 0;
    let awayGames = 0;
    let awayWins = 0;
    for (const m of filtered) {
      const us = Number(m.our_score);
      const them = Number(m.opponent_score);
      goalsFor += us;
      goalsAgainst += them;
      if (them === 0) cleanSheets += 1;
      if (us > them) wins += 1;
      else if (us === them) draws += 1;
      else losses += 1;
      if (m.home_away === "home") {
        homeGames += 1;
        if (us > them) homeWins += 1;
      } else if (m.home_away === "away") {
        awayGames += 1;
        if (us > them) awayWins += 1;
      }
    }
    const games = filtered.length;
    return {
      games,
      wins,
      draws,
      losses,
      goalsFor,
      goalsAgainst,
      goalDiff: goalsFor - goalsAgainst,
      cleanSheets,
      winRate: games > 0 ? Math.round((wins / games) * 100) : 0,
      avgFor: games > 0 ? goalsFor / games : 0,
      avgAgainst: games > 0 ? goalsAgainst / games : 0,
      homeGames,
      homeWins,
      awayGames,
      awayWins,
    };
  }, [filtered]);

  const monthlyBreakdown = useMemo(() => {
    const now = new Date(referenceNow ?? Date.now());
    const buckets: Array<{ key: string; label: string; wins: number; draws: number; losses: number; total: number }> = [];
    for (let i = 5; i >= 0; i -= 1) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
      buckets.push({
        key,
        label: date.toLocaleDateString(currentIntlLocale(), { month: "short" }),
        wins: 0,
        draws: 0,
        losses: 0,
        total: 0,
      });
    }
    const byKey = new Map(buckets.map((b) => [b.key, b]));
    for (const m of filtered) {
      if (!m.starts_at) continue;
      const date = new Date(m.starts_at);
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
      const bucket = byKey.get(key);
      if (!bucket) continue;
      const us = Number(m.our_score);
      const them = Number(m.opponent_score);
      if (us > them) bucket.wins += 1;
      else if (us === them) bucket.draws += 1;
      else bucket.losses += 1;
      bucket.total += 1;
    }
    return buckets;
  }, [filtered, referenceNow]);

  const streaks = useMemo(() => {
    const orderedAsc = [...filtered].reverse();
    let currentStreak = 0;
    let currentKind: "win" | "loss" | "draw" | null = null;
    let bestWin = 0;
    let bestUnbeaten = 0;
    let unbeaten = 0;
    for (const m of orderedAsc) {
      const us = Number(m.our_score);
      const them = Number(m.opponent_score);
      const kind = us > them ? "win" : us === them ? "draw" : "loss";
      if (currentKind === kind) currentStreak += 1;
      else {
        currentStreak = 1;
        currentKind = kind;
      }
      if (kind === "win") {
        bestWin = Math.max(bestWin, currentStreak);
        unbeaten += 1;
      } else if (kind === "draw") {
        unbeaten += 1;
      } else {
        unbeaten = 0;
      }
      bestUnbeaten = Math.max(bestUnbeaten, unbeaten);
    }
    return { current: { kind: currentKind, length: currentStreak }, bestWin, bestUnbeaten };
  }, [filtered]);

  const visibleMatches = filtered.filter((m) => {
    if (resultFilter === "all") return true;
    const us = Number(m.our_score);
    const them = Number(m.opponent_score);
    if (resultFilter === "win") return us > them;
    if (resultFilter === "draw") return us === them;
    return us < them;
  });

  if (isLoading) return <SectionLoading />;

  return (
    <div className="space-y-6">
      <section className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          {t("team.summary", { range: rangeLabel(range), count: totals.games })}
        </p>
        <div className="flex flex-wrap gap-2">
          <Select value={range} onValueChange={(value) => setRange(value as RangeKey)}>
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RANGE_KEYS.map((key) => (
                <SelectItem key={key} value={key}>
                  {rangeLabel(key)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={resultFilter} onValueChange={(value) => setResultFilter(value as typeof resultFilter)}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("team.filterAll")}</SelectItem>
              <SelectItem value="win">{t("team.filterWin")}</SelectItem>
              <SelectItem value="draw">{t("team.filterDraw")}</SelectItem>
              <SelectItem value="loss">{t("team.filterLoss")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </section>

      <StatStrip
        items={[
          { label: t("team.matches"), value: totals.games },
          { label: t("team.wins"), value: totals.wins, hint: t("team.winRateHint", { pct: totals.winRate }), tone: "text-[hsl(var(--ok))]" },
          { label: t("team.draws"), value: totals.draws },
          { label: t("team.losses"), value: totals.losses },
          { label: t("team.goalsFor"), value: totals.goalsFor, hint: t("team.avgPerMatch", { value: totals.avgFor.toFixed(2) }) },
          { label: t("team.goalsAgainst"), value: totals.goalsAgainst, hint: t("team.avgPerMatch", { value: totals.avgAgainst.toFixed(2) }) },
        ]}
      />

      <section className="grid lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">{t("team.trendTitle")}</CardTitle>
            <CardDescription>{t("team.trendDesc")}</CardDescription>
          </CardHeader>
          <CardContent>
            <MonthlyBreakdownChart data={monthlyBreakdown} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">{t("team.summaryTitle")}</CardTitle>
            <CardDescription>{t("team.summaryDesc")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <StatStrip
              className="bg-transparent p-0 grid-cols-2 lg:grid-cols-2"
              items={[
                { label: t("team.diff"), value: `${totals.goalDiff > 0 ? "+" : ""}${totals.goalDiff}`, tone: totals.goalDiff > 0 ? "text-[hsl(var(--ok))]" : totals.goalDiff < 0 ? "text-[hsl(var(--danger))]" : "" },
                { label: t("team.cleanSheet"), value: totals.cleanSheets },
                { label: t("team.homeWins"), value: `${totals.homeWins}/${totals.homeGames}` },
                { label: t("team.awayWins"), value: `${totals.awayWins}/${totals.awayGames}` },
              ]}
            />
            <Separator />
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="rounded-2xl bg-raised p-3">
                <p className="text-xs text-muted-foreground">{t("team.currentStreak")}</p>
                <p className="font-semibold">
                  {streaks.current.length === 0
                    ? "-"
                    : streaks.current.kind === "win"
                      ? t("team.consecutiveWin", { count: streaks.current.length })
                      : streaks.current.kind === "draw"
                        ? t("team.consecutiveDraw", { count: streaks.current.length })
                        : t("team.consecutiveLoss", { count: streaks.current.length })}
                </p>
              </div>
              <div className="rounded-2xl bg-raised p-3">
                <p className="text-xs text-muted-foreground">{t("team.bestStreak")}</p>
                <p className="font-semibold">{t("team.bestStreakLine", { wins: streaks.bestWin, unbeaten: streaks.bestUnbeaten })}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      <PlayersFullTable teamId={teamId} allSeasons={allSeasons} referenceNow={referenceNow} competitionId={competitionId} />

      <Card>
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-lg">{t("team.matchesTitle")}</CardTitle>
            <CardDescription>{t("team.matchesSubtitle", { count: visibleMatches.length })}</CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          {visibleMatches.length === 0 ? (
            <EmptyState text={t("team.emptyMatches")} />
          ) : (
            <div className="space-y-1.5 max-h-[40rem] overflow-y-auto pr-1">
              {visibleMatches.map((match) => {
                const us = Number(match.our_score);
                const them = Number(match.opponent_score);
                const outcome = us > them ? "win" : us === them ? "draw" : "loss";
                const assigned = goalsByMatch.get(match.id) || 0;
                return (
                  <div key={match.id} className="flex items-center gap-3 py-2.5 border-t first:border-t-0">
                    <div className={cn(
                      "h-9 w-9 rounded-xl flex items-center justify-center eb-num text-[20px] text-background shrink-0",
                      outcome === "win" && "bg-success",
                      outcome === "draw" && "bg-warning",
                      outcome === "loss" && "bg-destructive",
                    )}>
                      {outcome === "win" ? t("team.winsLetter") : outcome === "draw" ? t("team.drawsLetter") : t("team.lossesLetter")}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">vs {match.opponent_name}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {match.starts_at ? formatDateTime(match.starts_at) : t("team.dateNa")}
                        {match.competition_name ? ` · ${match.competition_name}` : ""} · {match.home_away}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <span className="eb-num text-[24px] pr-1">
                        {match.our_score}-{match.opponent_score}
                      </span>
                      {assigned !== us && (
                        <span className="text-xs text-amber-600">{t("team.assignedHint", { count: assigned })}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
